import {
  CreateMenuAiDraftResponse,
  GenerateMenuAiImageDto,
  GenerateMenuAiImageResponse,
  ImproveMenuAiDto,
  MenuAiDraftData,
  MenuAiDraftResponse,
  MenuAiLibraryImageResponse,
  MenuAiLibraryImagesParams,
  RefineMenuAiItemDto,
  SaveMenuAiDraftResponse,
} from '../types/api/menuAi.types';
import { ResponseType } from '../types/shared.types';
import { api } from './api';
import { MenuId } from './menu.api';

const menuIdParams = (menuId: MenuId) => ({
  params: menuId ? { menuId } : undefined,
});

/** The draft is tied to the given menu; saving it writes into that menu. */
export const improveMenuAi = (
  locationId: string | number,
  brandId: string | number,
  data: ImproveMenuAiDto,
  menuId?: MenuId
) =>
  api.post<ResponseType<CreateMenuAiDraftResponse>>(
    `/v1/menu-ai/${locationId}/${brandId}/improve`,
    data,
    menuIdParams(menuId)
  );

export const getMenuAiDraft = (locationId: string | number, draftId: string) =>
  api.get<ResponseType<MenuAiDraftResponse>>(
    `/v1/menu-ai/${locationId}/draft/${draftId}`
  );

// Menu-scoped: deletes whatever draft the backend currently holds for this
// menu, with no client-known id. Called on every modal open so each admin
// starts from a fresh menu (the draft is shared server-side across admins, and
// no browser can see another's local state). Assumed idempotent — a no-op with
// a success response when there's nothing to delete.
export const deleteMenuAiDraft = (
  locationId: string | number,
  brandId: string | number,
  menuId?: MenuId
) =>
  api.delete<ResponseType<null>>(
    `/v1/menu-ai/${locationId}/${brandId}/draft`,
    menuIdParams(menuId)
  );

export const refineMenuAiItem = (
  locationId: string | number,
  draftId: string,
  data: RefineMenuAiItemDto
) =>
  api.post<ResponseType<MenuAiDraftData>>(
    `/v1/menu-ai/${locationId}/draft/${draftId}/refine-item`,
    data
  );

export const saveMenuAiDraft = (locationId: string | number, draftId: string) =>
  api.post<ResponseType<SaveMenuAiDraftResponse>>(
    `/v1/menu-ai/${locationId}/draft/${draftId}/save`
  );

// Reverts a whole item (name/price/description/image/modifiers) back to the
// original menu. Returns the updated draft data, same shape as refine-item.
export const undoMenuAiItem = (
  locationId: string | number,
  draftId: string,
  itemId: number
) =>
  api.post<ResponseType<MenuAiDraftData>>(
    `/v1/menu-ai/${locationId}/draft/${draftId}/undo-item`,
    { itemId }
  );

export const generateMenuAiImage = (
  locationId: string | number,
  data: GenerateMenuAiImageDto
) =>
  api.post<ResponseType<GenerateMenuAiImageResponse>>(
    `/v1/menu-ai/${locationId}/generate-image`,
    data
  );

// Paginated, searchable library of previously generated images. Global by
// default (no location/brand filter) — pass those in `params` only to scope it.
export const getMenuAiLibraryImages = (params: MenuAiLibraryImagesParams) =>
  api.get<ResponseType<MenuAiLibraryImageResponse[]>>('/v1/menu-ai/images', {
    params,
  });

// Admin-only (enforced by the backend too) — removes a generated image from the
// global library.
export const deleteMenuAiLibraryImage = (imageId: string) =>
  api.delete<ResponseType<null>>(`/v1/menu-ai/images/${imageId}`);
